import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Avatar,
} from '@mui/material';
import {
  Dashboard,
  People,
  Assignment,
  Inventory,
  PersonAdd,
  Settings,
  Logout,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { ROUTES } from '../../config/constants';

interface AdminSidebarProps {
  onItemClick?: () => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ onItemClick }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    {
      text: t('navigation.dashboard'),
      icon: <Dashboard />,
      path: ROUTES.ADMIN_DASHBOARD,
    },
    {
      text: t('navigation.customers'),
      icon: <People />,
      path: ROUTES.ADMIN_CUSTOMERS,
    },
    {
      text: t('navigation.orders'),
      icon: <Assignment />,
      path: ROUTES.ADMIN_ORDERS,
    },
    {
      text: t('navigation.products'),
      icon: <Inventory />,
      path: ROUTES.ADMIN_PRODUCTS,
    },
    {
      text: t('navigation.users'),
      icon: <PersonAdd />,
      path: ROUTES.ADMIN_USERS,
    },
    {
      text: t('navigation.settings'),
      icon: <Settings />,
      path: ROUTES.ADMIN_SETTINGS,
    },
  ];

  const handleItemClick = (path: string) => {
    navigate(path);
    onItemClick?.();
  };

  const handleLogout = () => {
    logout();
    navigate(ROUTES.LOGIN);
    onItemClick?.();
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'primary.dark' }}>
      {/* Header */}
      <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.main' }}>
        <img
          src="/Mowilex-Corporate.png"
          alt="Mowilex Logo"
          style={{
            maxWidth: '140px',
            height: 'auto',
            marginBottom: '8px',
            filter: 'brightness(0) invert(1)' // Make logo white on dark background
          }}
        />
        <Typography variant="body2" color="rgba(255,255,255,0.7)">
          Admin Console
        </Typography>
      </Box>

      <Divider sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />

      {/* User Info */}
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Avatar sx={{ mx: 'auto', mb: 1, bgcolor: 'white', color: 'primary.main' }}>
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <Typography variant="subtitle2" color="white" noWrap>
          {user?.name}
        </Typography>
        <Typography variant="caption" color="rgba(255,255,255,0.7)" noWrap>
          {user?.role?.toUpperCase()}
        </Typography>
      </Box>

      <Divider sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />

      {/* Navigation */}
      <Box sx={{ flexGrow: 1 }}>
        <List>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={() => handleItemClick(item.path)}
                  selected={isActive}
                  sx={{
                    color: 'rgba(255,255,255,0.8)',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.2)',
                      },
                      '& .MuiListItemIcon-root': {
                        color: 'white',
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive ? 'white' : 'rgba(255,255,255,0.7)',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Divider sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />

      {/* Logout */}
      <List>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleLogout}
            sx={{ 
              color: 'rgba(255,255,255,0.8)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'rgba(255,255,255,0.7)' }}>
              <Logout />
            </ListItemIcon>
            <ListItemText primary={t('navigation.logout')} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );
};

export default AdminSidebar;